import React from 'react';
import {Frame, FrameData} from "types/Frame";
import {Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import Uploader from "components/Form/Uploader";
import FieldHeader from "components/Form/FieldHeader";
import Toggle from "components/Form/Toggle";
import FormControls from "components/FormControls";
import {SiteAttachmentFieldSet} from "../../../components/Form/Form/Fieldset";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

type Props = {
  frame?: Frame | null
  onSubmit: (values: FrameData, helpers: FormikHelpers<FrameData>) => any
}

// Component ----

function FrameForm({frame, onSubmit}: Props) {
  const {termFor} = useOperator();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
    asset: Yup.object().shape({
      id: Yup.number().moreThan(0, "Please upload an asset")
    })
  })

  const initialValues: FrameData = {
    name: frame?.name || '',
    asset: {id: frame?.asset.id || 0},
    published: frame?.is_published || false,
    site_ids: frame?.sites.map(site => site.id)
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />
  
            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        The frame's name and asset
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-4 xl:col-span-3">
                        <Field
                          label="Name *"
                          hint='255 chars max'
                          type="text"
                          name="name"
                          maxLength={255}
                          autoFocus
                        />
                      </div>
                      <div className="sm:col-span-6">
                        <FieldHeader label="Asset *" hint="Choose a square image with transparency"/>
                        <Uploader
                          files={frame?.asset}
                          type={Uploader.types.IMAGE}
                          maxFiles={1}
                          onChange={files => formik.setFieldValue('asset', {id: files[0]?.id || 0})}
                        />

                        {/* @ts-ignore */}
                        {(!!formik.errors?.asset?.id && formik.touched?.asset?.id) &&
                          <FieldHelp isError>
                            {/* @ts-ignore */}
                            {formik.errors.asset?.id}
                          </FieldHelp>
                        }
                      </div>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {termFor('Sites')}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose which {termFor('sites')} to associate this with.
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <SiteAttachmentFieldSet name={"site_ids"} selected={frame?.sites}/>
                      </div>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Publish
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Make this frame available to your audience by publishing it
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Published"
                          checked={formik.values.published}
                          onChange={checked => formik.setFieldValue('published', checked)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <FormControls formik={formik}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

export default FrameForm;